import type { JobFilter, JobRecord } from "../types/job.js";
import type { StorageAdapter } from "../types/storage.js";
import { SilentCronXError } from "../utils/errors.js";
import { nowIso } from "../utils/time.js";

type LockRecord = {
  expiresAt: number;
};

export class MemoryStorageAdapter implements StorageAdapter {
  private readonly jobs = new Map<string, JobRecord>();
  private readonly locks = new Map<string, LockRecord>();

  async saveJob(job: JobRecord): Promise<void> {
    if (this.jobs.has(job.id)) {
      throw new SilentCronXError(`Duplicate job id rejected: ${job.id}`);
    }
    this.jobs.set(job.id, { ...job });
  }

  async updateJob(jobId: string, patch: Partial<JobRecord>): Promise<void> {
    const existing = this.jobs.get(jobId);
    if (!existing) throw new SilentCronXError(`Job not found: ${jobId}`);
    this.jobs.set(jobId, { ...existing, ...patch, updatedAt: nowIso() });
  }

  async getJob(jobId: string): Promise<JobRecord | null> {
    const job = this.jobs.get(jobId);
    return job ? { ...job } : null;
  }

  async listJobs(filter: JobFilter = {}): Promise<JobRecord[]> {
    return [...this.jobs.values()]
      .filter((job) => !filter.status || job.status === filter.status)
      .filter((job) => !filter.name || job.name === filter.name)
      .filter((job) => !filter.kind || job.kind === filter.kind)
      .filter((job) => !filter.queueName || job.queueName === filter.queueName)
      .map((job) => ({ ...job }));
  }

  async deleteJob(jobId: string): Promise<void> {
    this.jobs.delete(jobId);
  }

  async acquireLock(lockKey: string, ttlMs: number): Promise<boolean> {
    this.cleanupExpiredLocks();
    const existing = this.locks.get(lockKey);
    if (existing && existing.expiresAt > Date.now()) return false;
    this.locks.set(lockKey, { expiresAt: Date.now() + ttlMs });
    return true;
  }

  async releaseLock(lockKey: string): Promise<void> {
    this.locks.delete(lockKey);
  }

  private cleanupExpiredLocks(): void {
    const now = Date.now();
    for (const [key, lock] of this.locks) {
      if (lock.expiresAt <= now) this.locks.delete(key);
    }
  }
}
